"use client";

import Link from "next/link";
import { useEffect, useState, type JSX } from "react";
import { ctaButtonClassName } from "@/lib/cta-styles";
import {
  getConsentState,
  hydrateConsentState,
  readLocalStorage,
  recordConsentDecision,
  subscribeToConsentState,
  type ConsentState,
} from "@/lib/consent";

/**
 * Global opt-in banner for Google Analytics, Contentsquare, and the chat
 * widget. Non-blocking by design — role="region", not role="dialog": a
 * visitor can keep reading/scrolling while it's up (unlike CalendlyModal,
 * which is a true blocking overlay).
 */
export function ConsentBanner(): JSX.Element | null {
  const [state, setState] = useState<ConsentState>(getConsentState());

  useEffect(() => {
    hydrateConsentState(readLocalStorage());
    return subscribeToConsentState(setState);
  }, []);

  const isOpen = state === "unset";

  // Give bottom-anchored floating CTAs extra clearance while this is open —
  // mirrors the existing body[data-chat-provider] mechanism in globals.css.
  useEffect(() => {
    if (!isOpen) return;
    document.body.setAttribute("data-consent-banner-open", "true");
    return () => document.body.removeAttribute("data-consent-banner-open");
  }, [isOpen]);

  if (!isOpen) return null;

  const decide = (status: "accepted" | "rejected") => {
    recordConsentDecision(readLocalStorage(), status);
  };

  return (
    <div
      role="region"
      aria-label="Cookie and tracking consent"
      className="fixed inset-x-0 bottom-0 z-[90] flex justify-center px-4 pb-4 sm:px-6"
    >
      <div className="flex w-full max-w-3xl flex-col gap-4 rounded-2xl border border-white/10 bg-[#0f1218] p-5 shadow-[0px_8px_40px_rgba(0,0,0,0.6)] sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-white/80">
          We use cookies and similar tracking (Google Analytics, Contentsquare,
          and our live chat widget) to understand site usage and provide
          support. These only load if you accept. See our{" "}
          <Link href="/privacy" className="underline hover:text-white">
            Privacy Policy
          </Link>{" "}
          for details.
        </p>
        <div className="flex shrink-0 gap-3">
          <button
            type="button"
            onClick={() => decide("rejected")}
            className={ctaButtonClassName("primary")}
          >
            Reject All
          </button>
          <button
            type="button"
            onClick={() => decide("accepted")}
            className={ctaButtonClassName("secondary")}
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
